export type IsoDayRange = { from: string; to: string };

export type DateRangeErrorCode = "MISSING" | "ORDER" | "FUTURE" | "TOO_LONG";

const MONTH_ABBREVIATIONS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const pad2 = (value: number) => String(value).padStart(2, "0");

export const toIsoDay = (date: Date): string =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

// "YYYY-MM-DD" -> local midnight Date; undefined when malformed or not a real calendar day.
export const parseIsoDay = (day?: string | null): Date | undefined => {
  if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return undefined;
  const [year, month, dayOfMonth] = day.split("-").map(Number);
  const date = new Date(year, month - 1, dayOfMonth);

  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === dayOfMonth
    ? date
    : undefined;
};

export const todayIsoDay = (now: Date = new Date()): string => toIsoDay(now);

export const addDays = (date: Date, days: number): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

// Whole calendar days from `fromDay` to `toDay` (same day = 0).
export const daysBetween = (fromDay: string, toDay: string): number | undefined => {
  const from = parseIsoDay(fromDay);
  const to = parseIsoDay(toDay);

  return from && to ? Math.round((to.getTime() - from.getTime()) / 86_400_000) : undefined;
};

// monthOffset 0 = 1st of the current month -> today; negative offsets = the whole earlier month.
export const calendarMonthRange = (monthOffset: number, now: Date = new Date()): IsoDayRange => {
  const first = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const end = monthOffset === 0 ? now : new Date(first.getFullYear(), first.getMonth() + 1, 0);

  return { from: toIsoDay(first), to: toIsoDay(end) };
};

export const getDateRangeError = (
  from?: string,
  to?: string,
  options: { today?: string; maxDays?: number } = {}
): DateRangeErrorCode | undefined => {
  const fromDate = parseIsoDay(from);
  const toDate = parseIsoDay(to);

  if (!fromDate || !toDate) return "MISSING";
  if (fromDate > toDate) return "ORDER";
  if (toIsoDay(toDate) > (options.today ?? todayIsoDay())) return "FUTURE";
  if (options.maxDays !== undefined && (daysBetween(toIsoDay(fromDate), toIsoDay(toDate)) ?? 0) > options.maxDays) {
    return "TOO_LONG";
  }

  return undefined;
};

// "2026-09-01" -> "1 Sep 2026"
export const formatIsoDayLabel = (day?: string | null): string => {
  const date = parseIsoDay(day);

  return date ? `${date.getDate()} ${MONTH_ABBREVIATIONS[date.getMonth()]} ${date.getFullYear()}` : "";
};
